import React, { forwardRef, useMemo } from "react";
import {
    Container,
    Paper,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    useTheme,
} from "@mui/material";
import {
    PieChart,
    Pie,
    Tooltip,
    Legend,
    Cell
} from "recharts";

// Utility functions
const getStatusName = (statusCode) => {
    switch (statusCode) {
        case 0: return 'In Progress';
        case 1: return 'Pending Payment';
        case 2: return 'Completed';
        case 5: return 'Cancelled';
        default: return 'Unknown';
    }
};

const getOrderTypeName = (typeCode) => {
    switch (typeCode) {
        case 0: return 'In-store';
        case 1: return 'Pickup';
        case 2: return 'Kiosk';
        case 3: return 'Online';
        default: return 'Other';
    }
};

const payTypeMapReverse = { 0: 'Cash', 1: 'Gift Card', 2: 'Online', 3: 'Credit Card', 4: 'Other' };
const safeRound = (num) => parseFloat(num.toFixed(2));

// --- DATA PROCESSING UTILITY (Refactored for flat Order Array) ---
const processTipReportData = (allOrders) => {
    // allOrders is now expected to be a flat array of OrderSchema documents.
    if (!allOrders || !Array.isArray(allOrders) || allOrders.length === 0) {
        return { tipSummary: [], detailedReport: [], orderTypeTips: {}, paymentComparison: {}, totalTips: 0 };
    }

    // Initialize accumulation maps with safe zero values
    const tipSummaryMap = { 'Cash': 0, 'Credit Card': 0, 'Online': 0, 'Gift Card': 0 };
    const orderTypeTipsMap = { 'In-store': 0, 'Pickup': 0, 'Kiosk': 0, 'Online': 0, 'Other': 0 };
    const paymentComparisonMap = {
        'Cash': { sales: 0, tips: 0 },
        'Credit Card': { sales: 0, tips: 0 },
        'Online': { sales: 0, tips: 0 },
        'Gift Card': { sales: 0, tips: 0 },
    };

    const detailedReport = [];
    let totalTips = 0;

    // 1. Filter and iterate only over Completed orders (oStatus === 2)
    const completedOrders = allOrders.filter(order => order.oStatus === 2);

    completedOrders.forEach(order => {
        const orderTip = order.Tip ?? 0;
        const orderTotal = order.total ?? 0;
        
        totalTips += orderTip;
        const orderType = getOrderTypeName(order.type);

        // 1. Accumulate tips by Order Type
        orderTypeTipsMap[orderType] += orderTip;

        // 2. Accumulate by Payment Type
        order.payment?.forEach(payment => {
            const payType = payment.payType;
            const paymentTypeName = payTypeMapReverse[payType] || 'Other';
            const tip = payment.tip ?? 0;
            const amount = payment.amount ?? 0;
            
            if (paymentComparisonMap.hasOwnProperty(paymentTypeName)) {
                paymentComparisonMap[paymentTypeName].sales += amount;
                paymentComparisonMap[paymentTypeName].tips += tip;
                tipSummaryMap[paymentTypeName] += tip;
            }
        });

        // 3. Detailed Report Row
        detailedReport.push({
            orderID: order.orderNo,
            totalAmount: orderTotal,
            tip: orderTip,
            // If there are multiple payments, this grabs the pay type of the first one listed.
            paymentType: order.payment?.[0] ? payTypeMapReverse[order.payment[0].payType] : 'N/A', 
            status: getStatusName(order.oStatus),
            orderType: orderType,
        });
    });

    // 4. Finalize totals by rounding and mapping
    const tipSummary = Object.keys(tipSummaryMap)
        .filter(key => tipSummaryMap[key] > 0)
        .map(key => ({
            name: key,
            value: safeRound(tipSummaryMap[key])
        }));

    return { 
        tipSummary, 
        detailedReport, 
        orderTypeTips: orderTypeTipsMap,
        paymentComparison: Object.entries(paymentComparisonMap).reduce((acc, [type, data]) => {
            acc[type] = { sales: safeRound(data.sales), tips: safeRound(data.tips) };
            return acc;
        }, {}),
        totalTips: safeRound(totalTips)
    };
};

// --- Main Component ---
export const TipReportPage = forwardRef(({ rawData }, ref) => {
    const theme = useTheme();

    // --- CENTRAL DATA ORCHESTRATION ---
    const reportData = useMemo(() => {
        let finalDataArray = []; // This will hold the flat array of orders

        // 1. Check Session Storage if prop is empty/incomplete
        if (!rawData || !rawData.orders || rawData.orders.length === 0) {
            const stored = sessionStorage.getItem("tipReportData");
            if (stored) {
                try {
                    const parsed = JSON.parse(stored);
                    
                    // FIX: Access the nested 'orders' array from the sessionStorage payload
                    if (parsed.rawData && parsed.rawData.orders) {
                        finalDataArray = parsed.rawData.orders; 
                    }
                    
                } catch (err) {
                    console.error("Failed to parse stored tip report data:", err);
                    finalDataArray = [];
                }
            }
        } else {
            // FIX: If rawData prop exists, assume it is the full server object and pull the orders array
            finalDataArray = rawData.orders || [];
        }
        
        // 2. Process the finalized data structure (which is a flat array of orders)
        return processTipReportData(finalDataArray);

    }, [rawData]);

    const COLORS = [
        theme.palette.primary.main, // Blue
        theme.palette.success.main, // Green
        theme.palette.warning.main, // Orange
        theme.palette.secondary.main, // Purple
    ];

    if (!reportData || reportData.detailedReport.length === 0) {
        return (
            <Container maxWidth="xl" sx={{ py: 4 }} ref={ref}>
                <Paper elevation={3} sx={{ p: 3, my: 3, textAlign: 'center' }}>
                    <Typography variant="h6" color="text.secondary">
                        No tip data available for this report.
                    </Typography>
                </Paper>
            </Container>
        );
    }

    const tipByOrderTypeData = Object.entries(reportData.orderTypeTips)
        .filter(([, tips]) => tips > 0)
        .map(([type, tips]) => ({ type, tips: tips.toFixed(2) }));
        
    const paymentComparisonData = Object.entries(reportData.paymentComparison)
        .filter(([, data]) => data.sales > 0 || data.tips > 0)
        .map(([type, data]) => ({ 
            type, 
            sales: data.sales.toFixed(2), 
            tips: data.tips.toFixed(2),
            // Calculate Tip Rate safely
            tipRate: data.sales > 0 ? ((data.tips / data.sales) * 100).toFixed(2) + '%' : '0.00%'
        }));

    return (
        <Container maxWidth="xl" sx={{ py: 4 }} ref={ref}>
            {/* --- VISUAL SUMMARY & PRIMARY COMPARISON --- */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, mb: 4 }}>
                {/* 1. Pie Chart Section */}
                <Paper elevation={3} sx={{ p: 3, flexGrow: 1, minWidth: '450px', backgroundColor: '#e8f5e9' }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Tip Summary by Payment Type</Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <PieChart width={400} height={300}>
                            <Pie
                                data={reportData.tipSummary}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                outerRadius={100}
                                fill="#8884d8"
                                label
                            >
                                {reportData.tipSummary.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip formatter={(value) => `$${value.toFixed(2)}`} />
                            <Legend />
                        </PieChart>
                    </Box>
                    <Typography variant="h6" align="center" mt={2} color="primary">
                        Total Tips: **${reportData.totalTips.toFixed(2)}**
                    </Typography>
                </Paper>

                {/* 2. Tips by Order Type Table */}
                <Paper elevation={3} sx={{ p: 3, flexGrow: 1, minWidth: '450px', backgroundColor: '#fff3e0' }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Tips by Order Type</Typography>
                    <TableContainer component={Box} sx={{ maxHeight: 350, mb: 1 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: '#fffaf5' }}>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Order Type</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Total Tips</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {tipByOrderTypeData.map((row, index) => (
                                    <TableRow
                                        key={index}
                                        sx={{ 
                                            '&:nth-of-type(odd)': { backgroundColor: '#fdf3e6' }, 
                                            '&:hover': { backgroundColor: '#fce8c7', cursor: 'pointer' } 
                                        }}
                                    >
                                        <TableCell>{row.type}</TableCell>
                                        <TableCell align="right">${row.tips}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Box>

            {/* --- DETAILED COMPARISON TABLE (Tips vs Sales) --- */}
            <Paper elevation={3} sx={{ p: 3, minWidth: '450px', mb: 4, backgroundColor: '#f3e5f5' }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Payment Method Tip Rate Comparison</Typography>
                <TableContainer component={Box} >
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Payment Method</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Total Sales</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Total Tips</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Tip Rate</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paymentComparisonData.map((row, index) => (
                                <TableRow
                                    key={index}
                                    sx={{
                                        '&:nth-of-type(odd)': { backgroundColor: '#f9f3fb' },
                                        '&:hover': { backgroundColor: '#ede1f4', cursor: 'pointer' }
                                    }}
                                >
                                    <TableCell>**{row.type}**</TableCell>
                                    <TableCell align="right">${row.sales}</TableCell>
                                    <TableCell align="right">${row.tips}</TableCell>
                                    <TableCell align="right">{row.tipRate}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* --- DETAILED ORDER LOG TABLE --- */}
            <Paper elevation={3} sx={{ p: 3, minWidth: '450px', backgroundColor: '#e3f2fd' }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Detailed Order Log</Typography>
                <TableContainer component={Box}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Order ID</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Order Type</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Total Amount</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Tip</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Payment Type</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {reportData.detailedReport.map((row, index) => (
                                <TableRow
                                    key={index}
                                    sx={{
                                        '&:nth-of-type(odd)': { backgroundColor: '#f8f8f8' },
                                        '&:hover': { backgroundColor: '#e0e0e0', cursor: 'pointer' },
                                        backgroundColor: row.status === 'Cancelled' ? '#ffebee' : 'inherit' 
                                    }}
                                >
                                    <TableCell>{row.orderID}</TableCell>
                                    <TableCell>{row.orderType}</TableCell> 
                                    <TableCell>
                                        <Box component="span" sx={{
                                            fontWeight: 'bold',
                                            color: row.status === 'Completed' ? 'success.dark' : row.status === 'Cancelled' ? 'error.dark' : 'warning.dark'
                                        }}>
                                            {row.status}
                                        </Box>
                                    </TableCell>
                                    <TableCell>${row.totalAmount?.toFixed(2) ?? '0.00'}</TableCell>
                                    <TableCell>${row.tip?.toFixed(2) ?? '0.00'}</TableCell>
                                    <TableCell>{row.paymentType}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

        </Container>
    );
});